import type { Vector3Like } from 'three'

/**
 * Hard cap on points persisted per free-line element. CatmullRomCurve3 + TubeGeometry
 * (capped at 64 tubular segments in free.tsx) cannot resolve more than ~64 control points
 * visually, so anything beyond this is pure payload bloat.
 */
const MAX_POINTS_BALANCED = 256

/**
 * Points beyond this threshold trigger backfill simplification on hydrate. Slightly
 * higher than the cap so we don't churn drawings that are already near the limit.
 */
const BACKFILL_THRESHOLD = 300

/**
 * Decimal places kept when serializing coordinates. The body model is roughly in the
 * 1-100 unit range; 4 decimals gives ~0.0001 unit precision (well below pixel-rendered
 * resolution). Cuts ~40% off JSON size vs full f64 precision.
 */
const COORD_PRECISION = 4

/**
 * RDP epsilon (world-space distance) used to drop colinear points. Tuned so visually
 * imperceptible deviations are removed but real shape inflections survive.
 */
const RDP_EPSILON = 0.0025

/**
 * Minimum world-space distance between successive captured points during draw.
 * Scaled by line width (size param, 2-5; world radius = size/100) so the gate matches
 * stroke thickness — points closer than half a tube radius cannot be visually distinguished.
 */
export const getMinCaptureDistanceSq = (lineWidth: number): number => {
  const radius = lineWidth / 100
  const minDist = radius * 0.5
  return minDist * minDist
}

export const distanceSq = (a: Vector3Like, b: Vector3Like): number => {
  const dx = a.x - b.x
  const dy = a.y - b.y
  const dz = a.z - b.z
  return dx * dx + dy * dy + dz * dz
}

const quantize = (n: number): number => {
  const factor = 10 ** COORD_PRECISION
  return Math.round(n * factor) / factor
}

const quantizePoint = (p: Vector3Like): Vector3Like => ({
  x: quantize(p.x),
  y: quantize(p.y),
  z: quantize(p.z),
})

/**
 * Perpendicular distance from point P to the line segment AB in 3D, squared.
 * Returns squared distance to keep things alloc-free in the hot loop.
 */
const perpendicularDistanceSq = (p: Vector3Like, a: Vector3Like, b: Vector3Like): number => {
  const abx = b.x - a.x
  const aby = b.y - a.y
  const abz = b.z - a.z
  const apx = p.x - a.x
  const apy = p.y - a.y
  const apz = p.z - a.z

  const abLenSq = abx * abx + aby * aby + abz * abz
  if (abLenSq === 0) {
    return apx * apx + apy * apy + apz * apz
  }

  // Cross product magnitude = |AB x AP|; perpendicular distance = |AB x AP| / |AB|.
  const cx = aby * apz - abz * apy
  const cy = abz * apx - abx * apz
  const cz = abx * apy - aby * apx
  const crossSq = cx * cx + cy * cy + cz * cz

  return crossSq / abLenSq
}

/**
 * Iterative Ramer-Douglas-Peucker simplification. Drops points whose perpendicular
 * deviation from the surrounding chord is below `epsilon`. Iterative (stack-based) to
 * avoid recursion blow-up at very high N.
 */
const rdpSimplify = (points: Vector3Like[], epsilon: number): Vector3Like[] => {
  const n = points.length
  if (n <= 2) return points.slice()

  const epsilonSq = epsilon * epsilon
  const keep = new Uint8Array(n)
  keep[0] = 1
  keep[n - 1] = 1

  const stack: Array<[number, number]> = [[0, n - 1]]
  while (stack.length > 0) {
    const [start, end] = stack.pop()!
    if (end - start < 2) continue

    let maxDistSq = 0
    let maxIdx = -1
    const a = points[start]
    const b = points[end]
    for (let i = start + 1; i < end; i++) {
      const d = perpendicularDistanceSq(points[i], a, b)
      if (d > maxDistSq) {
        maxDistSq = d
        maxIdx = i
      }
    }

    if (maxIdx !== -1 && maxDistSq > epsilonSq) {
      keep[maxIdx] = 1
      stack.push([start, maxIdx])
      stack.push([maxIdx, end])
    }
  }

  const out: Vector3Like[] = []
  for (let i = 0; i < n; i++) {
    if (keep[i]) out.push(points[i])
  }
  return out
}

/**
 * If RDP didn't bring the count under `max`, uniform-stride decimate while keeping
 * first and last point. Guarantees a bounded output size.
 */
const decimateToMax = (points: Vector3Like[], max: number): Vector3Like[] => {
  if (points.length <= max) return points
  const out: Vector3Like[] = new Array(max)
  const step = (points.length - 1) / (max - 1)
  for (let i = 0; i < max - 1; i++) {
    out[i] = points[Math.round(i * step)]
  }
  out[max - 1] = points[points.length - 1]
  return out
}

/**
 * Full reduction pipeline for a freshly-drawn or loaded free-line: RDP -> hard cap ->
 * coordinate quantization. Always returns a new array of plain `{x,y,z}` objects
 * (safe to send over the wire).
 */
export const simplifyFreeLinePoints = (points: Vector3Like[]): Vector3Like[] => {
  if (points.length === 0) return []
  if (points.length === 1) return [quantizePoint(points[0])]

  const reduced = rdpSimplify(points, RDP_EPSILON)
  const capped = decimateToMax(reduced, MAX_POINTS_BALANCED)
  return capped.map(quantizePoint)
}

/**
 * Whether a hydrated element's points array is large enough to warrant backfill
 * simplification. Backfilled elements get marked `isModified` so the next autosave
 * cycle persists the smaller version.
 */
export const shouldBackfillSimplify = (pointCount: number): boolean =>
  pointCount > BACKFILL_THRESHOLD
